import { useMemo } from "react";
import styled from "styled-components";

import { RAID_SORT_ORDER } from "@core/constants";
import { useCustomTodos } from "@core/hooks/queries/todo";
import type { Character, TodoRaid } from "@core/types/character";
import type { CustomTodoItem } from "@core/types/todo";

import CharacterCell from "./cells/CharacterCell";
import CounterCell from "./cells/CounterCell";
import CustomCell from "./cells/CustomCell";
import DayCheckCell from "./cells/DayCheckCell";
import RaidCell from "./cells/RaidCell";
import TotalGoldCell from "./cells/TotalGoldCell";
import WeekCheckCell from "./cells/WeekCheckCell";

interface Props {
  characters: Character[];
}

interface ColumnVisibility {
  chaos: boolean;
  guardian: boolean;
  silmael: boolean;
  elysian: boolean;
  halHourglass: boolean;
  hellKey: boolean;
  cubeTicket: boolean;
}

const computeColumnVisibility = (
  characters: Character[]
): ColumnVisibility => ({
  // 전역 OR: 화면의 캐릭터 중 1명이라도 showXxx=true면 열 표시
  chaos: characters.some((c) => c.settings.showChaos),
  guardian: characters.some((c) => c.settings.showGuardian),
  silmael: characters.some((c) => c.settings.showSilmaelChange),
  elysian: characters.some((c) => c.settings.showElysian),
  halHourglass: characters.some((c) => c.settings.showHalHourglass),
  hellKey: characters.some((c) => c.settings.showHellKey),
  cubeTicket: characters.some((c) => c.settings.showCubeTicket),
});

/**
 * 모든 캐릭터가 등록한 주간 레이드 카테고리의 합집합을 계산하고
 * RAID_SORT_ORDER 기준으로 정렬해서 반환한다.
 * - 일부 캐릭터는 settings.showWeekTodo=false일 수 있는데, 열 합집합 기준에서는 "한 명이라도 true"여야 함
 */
const computeRaidColumns = (characters: Character[]): string[] => {
  const set = new Set<string>();
  characters.forEach((c) => {
    if (!c.settings.showWeekTodo) return;
    c.todoList.forEach((t) => set.add(t.weekCategory));
  });
  const unsorted = Array.from(set);
  // RAID_SORT_ORDER에 포함된 것 먼저 정렬, 없으면 뒤에
  const orderIndex = new Map<string, number>();
  RAID_SORT_ORDER.forEach((name, i) => orderIndex.set(name, i));
  return unsorted.sort((a, b) => {
    const ia = orderIndex.has(a) ? (orderIndex.get(a) as number) : 9999;
    const ib = orderIndex.has(b) ? (orderIndex.get(b) as number) : 9999;
    if (ia !== ib) return ia - ib;
    return a.localeCompare(b, "ko");
  });
};

/**
 * 캐릭터 단위로 weekCategory → TodoRaid lookup 맵 생성.
 */
const buildRaidLookup = (
  characters: Character[]
): Map<number, Map<string, TodoRaid>> => {
  const map = new Map<number, Map<string, TodoRaid>>();
  characters.forEach((c) => {
    const inner = new Map<string, TodoRaid>();
    c.todoList.forEach((t) => inner.set(t.weekCategory, t));
    map.set(c.characterId, inner);
  });
  return map;
};

const dayColCount = (c: ColumnVisibility) =>
  (c.chaos ? 1 : 0) + (c.guardian ? 1 : 0);

const weekColCount = (c: ColumnVisibility) =>
  (c.silmael ? 1 : 0) + (c.elysian ? 1 : 0) + (c.halHourglass ? 1 : 0);

const resourceColCount = (c: ColumnVisibility) =>
  (c.hellKey ? 1 : 0) + (c.cubeTicket ? 1 : 0);

const fmtGold = (n: number) =>
  n.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

/**
 * 합계 행의 주간 수익 총합 셀.
 */
const SummaryTotal = ({ characters }: { characters: Character[] }) => {
  const { current, expected } = useMemo(() => {
    let cur = 0;
    let exp = 0;
    characters.forEach((c) => {
      cur += c.weekRaidGold;
      c.todoList.forEach((t) => {
        exp += t.realGold;
      });
    });
    return { current: cur, expected: exp };
  }, [characters]);

  const isDone = expected > 0 && current === expected;
  return (
    <SummaryGold $isDone={isDone}>
      {fmtGold(current)} / {fmtGold(expected)} G
    </SummaryGold>
  );
};

/**
 * 표(Table) 형식 숙제 뷰.
 *
 * 열 구성 (Phase B):
 *   - 캐릭터 (sticky left)
 *   - 일일: 혼돈 / 가디언
 *   - 주간: 실마엘 / 낙원 / 할모래
 *   - 자원: 지옥열쇠 / 큐브티켓
 * 열 가시성: 전역 OR (한 명이라도 show=true면 열 표시, 꺼진 캐릭터는 '-')
 */
const TodoTable = ({ characters }: Props) => {
  const cols = useMemo(
    () => computeColumnVisibility(characters),
    [characters]
  );
  const raidColumns = useMemo(
    () => computeRaidColumns(characters),
    [characters]
  );
  const raidLookup = useMemo(() => buildRaidLookup(characters), [characters]);
  const raidGroupVisible = raidColumns.length > 0;

  const { data: customTodos } = useCustomTodos(undefined);

  // WEEKLY 커스텀 숙제의 contentName 합집합 (열로 사용)
  const customColumns = useMemo(() => {
    if (!customTodos) return [] as string[];
    const set = new Set<string>();
    customTodos.forEach((t) => {
      if (t.frequency === "WEEKLY") set.add(t.contentName);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, "ko"));
  }, [customTodos]);

  // characterId → contentName → CustomTodoItem
  const customLookup = useMemo(() => {
    const map = new Map<number, Map<string, CustomTodoItem>>();
    if (customTodos) {
      customTodos.forEach((t) => {
        if (t.frequency !== "WEEKLY") return;
        let inner = map.get(t.characterId);
        if (!inner) {
          inner = new Map();
          map.set(t.characterId, inner);
        }
        inner.set(t.contentName, t);
      });
    }
    return map;
  }, [customTodos]);

  const customGroupVisible = customColumns.length > 0;

  if (characters.length === 0) {
    return (
      <Empty>
        <h3>표 뷰</h3>
        <p>표시할 캐릭터가 없습니다.</p>
      </Empty>
    );
  }

  // 주간 그룹/자원 그룹의 표시 여부 (헤더의 group label 출력 제어용)
  const weekGroupVisible = cols.silmael || cols.elysian || cols.halHourglass;
  const resourceGroupVisible = cols.hellKey || cols.cubeTicket;
  const dayGroupVisible = cols.chaos || cols.guardian;

  return (
    <Scroll>
      <Table>
        <colgroup>
          <col style={{ width: 200 }} />
          {cols.chaos && <col style={{ width: 80 }} />}
          {cols.guardian && <col style={{ width: 80 }} />}
          {raidColumns.map((name) => (
            <col key={`raidcol-${name}`} style={{ width: 130 }} />
          ))}
          {cols.silmael && <col style={{ width: 70 }} />}
          {cols.elysian && <col style={{ width: 100 }} />}
          {cols.halHourglass && <col style={{ width: 70 }} />}
          {customColumns.map((name) => (
            <col key={`customcol-${name}`} style={{ width: 90 }} />
          ))}
          {cols.hellKey && <col style={{ width: 100 }} />}
          {cols.cubeTicket && <col style={{ width: 100 }} />}
          <col style={{ width: 140 }} />
        </colgroup>
        <thead>
          <GroupRow>
            <GroupCharacterTh rowSpan={2}>캐릭터</GroupCharacterTh>
            {dayGroupVisible && (
              <GroupTh colSpan={dayColCount(cols)}>일일</GroupTh>
            )}
            {raidGroupVisible && (
              <GroupTh colSpan={raidColumns.length}>주간 레이드</GroupTh>
            )}
            {weekGroupVisible && (
              <GroupTh colSpan={weekColCount(cols)}>주간 숙제</GroupTh>
            )}
            {customGroupVisible && (
              <GroupTh colSpan={customColumns.length}>커스텀</GroupTh>
            )}
            {resourceGroupVisible && (
              <GroupTh colSpan={resourceColCount(cols)}>보유 자원</GroupTh>
            )}
            <GroupTotalTh rowSpan={2}>주간 수익</GroupTotalTh>
          </GroupRow>
          <HeaderRow>
            {cols.chaos && <ColTh>혼돈</ColTh>}
            {cols.guardian && <ColTh>가디언</ColTh>}
            {raidColumns.map((name) => (
              <ColTh key={`raidth-${name}`} title={name}>
                <RaidHeaderLabel>{name}</RaidHeaderLabel>
              </ColTh>
            ))}
            {cols.silmael && <ColTh>실마엘</ColTh>}
            {cols.elysian && <ColTh>낙원</ColTh>}
            {cols.halHourglass && <ColTh>할모래</ColTh>}
            {customColumns.map((name) => (
              <ColTh key={`customth-${name}`} title={name}>
                <RaidHeaderLabel>{name}</RaidHeaderLabel>
              </ColTh>
            ))}
            {cols.hellKey && <ColTh>지옥 열쇠</ColTh>}
            {cols.cubeTicket && <ColTh>큐브 티켓</ColTh>}
          </HeaderRow>
        </thead>
        <tbody>
          {characters.map((character) => {
            const perCharRaids = raidLookup.get(character.characterId);
            return (
              <BodyRow key={character.characterId}>
                <CharacterTd>
                  <CharacterCell character={character} />
                </CharacterTd>
                {cols.chaos && (
                  <Td>
                    <DayCheckCell character={character} category="chaos" />
                  </Td>
                )}
                {cols.guardian && (
                  <Td>
                    <DayCheckCell character={character} category="guardian" />
                  </Td>
                )}
                {raidColumns.map((raidName) => {
                  const todo =
                    character.settings.showWeekTodo && perCharRaids
                      ? (perCharRaids.get(raidName) ?? null)
                      : null;
                  return (
                    <Td key={`raidtd-${character.characterId}-${raidName}`}>
                      <RaidCell character={character} todo={todo} />
                    </Td>
                  );
                })}
                {cols.silmael && (
                  <Td>
                    <WeekCheckCell
                      character={character}
                      type="silmaelChange"
                    />
                  </Td>
                )}
                {cols.elysian && (
                  <Td>
                    <CounterCell character={character} type="elysian" />
                  </Td>
                )}
                {cols.halHourglass && (
                  <Td>
                    <WeekCheckCell character={character} type="halHourglass" />
                  </Td>
                )}
                {customColumns.map((name) => {
                  const item =
                    customLookup.get(character.characterId)?.get(name) ?? null;
                  return (
                    <Td
                      key={`customtd-${character.characterId}-${name}`}
                    >
                      <CustomCell
                        characterId={character.characterId}
                        customTodo={item}
                      />
                    </Td>
                  );
                })}
                {cols.hellKey && (
                  <Td>
                    <CounterCell character={character} type="hellKey" />
                  </Td>
                )}
                {cols.cubeTicket && (
                  <Td>
                    <CounterCell character={character} type="cubeTicket" />
                  </Td>
                )}
                <TotalTd>
                  <TotalGoldCell character={character} />
                </TotalTd>
              </BodyRow>
            );
          })}
          <SummaryRow>
            <SummaryLabelTd
              colSpan={
                1 +
                dayColCount(cols) +
                raidColumns.length +
                weekColCount(cols) +
                customColumns.length +
                resourceColCount(cols)
              }
            >
              합계
            </SummaryLabelTd>
            <SummaryTotalTd>
              <SummaryTotal characters={characters} />
            </SummaryTotalTd>
          </SummaryRow>
        </tbody>
      </Table>
    </Scroll>
  );
};

export default TodoTable;

const Scroll = styled.div`
  width: 100%;
  overflow-x: auto;
  overscroll-behavior-x: contain;
  border: 1px solid ${({ theme }) => theme.app.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.app.bg.white};

  /* 스크롤바를 두껍고 항상 보이게 — 넓은 테이블에서 가로 스크롤 위치 체감 쉽도록 */
  &::-webkit-scrollbar {
    height: 10px;
  }
  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.app.bg.gray1};
    border-radius: 0 0 8px 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.app.bg.gray2};
    border-radius: 6px;
    border: 2px solid ${({ theme }) => theme.app.bg.gray1};
  }
  &::-webkit-scrollbar-thumb:hover {
    background: ${({ theme }) => theme.app.text.light1};
  }
  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) =>
    `${theme.app.bg.gray2} ${theme.app.bg.gray1}`};
`;

const Table = styled.table`
  width: 100%;
  /* colgroup으로 각 열 폭을 명시했으므로 fixed — 렌더 일관성 + 성능 향상 */
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.app.text.dark2};
`;

const GroupRow = styled.tr`
  background: ${({ theme }) => theme.app.bg.gray1};
`;

const HeaderRow = styled.tr`
  background: ${({ theme }) => theme.app.bg.gray1};
`;

/* 2단 sticky header 높이 — ColTh의 top은 반드시 GroupRow 높이와 일치해야 함 */
const GROUP_ROW_HEIGHT = 29;

const GroupCharacterTh = styled.th`
  position: sticky;
  left: 0;
  top: 0;
  z-index: 4;
  height: ${GROUP_ROW_HEIGHT}px;
  padding: 4px 12px;
  text-align: left;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.app.text.dark2};
  background: ${({ theme }) => theme.app.bg.gray1};
  border-bottom: 1px solid ${({ theme }) => theme.app.border};
  border-right: 1px solid ${({ theme }) => theme.app.border};
`;

const GroupTh = styled.th`
  position: sticky;
  top: 0;
  z-index: 3;
  height: ${GROUP_ROW_HEIGHT}px;
  padding: 4px 8px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.app.text.dark2};
  background: ${({ theme }) => theme.app.bg.gray1};
  border-bottom: 1px solid ${({ theme }) => theme.app.border};

  & + & {
    border-left: 1px solid ${({ theme }) => theme.app.border};
  }
`;

const ColTh = styled.th`
  position: sticky;
  top: ${GROUP_ROW_HEIGHT}px;
  z-index: 3;
  padding: 6px 6px;
  text-align: center;
  font-size: 11px;
  font-weight: 600;
  /* 다크모드 대비 확보 위해 main 사용 (light: #444, dark: #fff) */
  color: ${({ theme }) => theme.app.text.main};
  background: ${({ theme }) => theme.app.bg.gray1};
  border-bottom: 1px solid ${({ theme }) => theme.app.border};

  &:not(:last-child) {
    border-right: 1px solid ${({ theme }) => theme.app.border};
  }
`;

const RaidHeaderLabel = styled.span`
  display: inline-block;
  max-width: 100%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BodyRow = styled.tr`
  &:hover {
    background: ${({ theme }) => theme.app.bg.gray1};
  }

  &:not(:last-child) td {
    border-bottom: 1px solid ${({ theme }) => theme.app.border};
  }
`;

const CharacterTd = styled.td`
  position: sticky;
  left: 0;
  z-index: 2;
  padding: 0;
  background: ${({ theme }) => theme.app.bg.white};
  border-right: 1px solid ${({ theme }) => theme.app.border};

  tr:hover & {
    background: ${({ theme }) => theme.app.bg.gray1};
  }
`;

const Td = styled.td`
  padding: 0;
  text-align: center;
  vertical-align: middle;

  &:not(:last-child) {
    border-right: 1px solid ${({ theme }) => theme.app.border};
  }
`;

const GroupTotalTh = styled.th`
  position: sticky;
  top: 0;
  right: 0;
  z-index: 4;
  padding: 6px 10px;
  text-align: center;
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.app.text.dark2};
  background: ${({ theme }) => theme.app.bg.gray1};
  border-bottom: 1px solid ${({ theme }) => theme.app.border};
  border-left: 1px solid ${({ theme }) => theme.app.border};
`;

const TotalTd = styled.td`
  position: sticky;
  right: 0;
  z-index: 1;
  padding: 0;
  background: ${({ theme }) => theme.app.bg.white};
  border-left: 1px solid ${({ theme }) => theme.app.border};

  tr:hover & {
    background: ${({ theme }) => theme.app.bg.gray1};
  }
`;

const SummaryRow = styled.tr`
  background: ${({ theme }) => theme.app.bg.gray1};
`;

const SummaryLabelTd = styled.td`
  position: sticky;
  left: 0;
  z-index: 2;
  padding: 8px 12px;
  background: ${({ theme }) => theme.app.bg.gray1};
  border-top: 2px solid ${({ theme }) => theme.app.border};
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.app.text.dark2};
  text-align: right;
`;

const SummaryTotalTd = styled.td`
  position: sticky;
  right: 0;
  z-index: 2;
  padding: 8px 10px;
  background: ${({ theme }) => theme.app.bg.gray1};
  border-top: 2px solid ${({ theme }) => theme.app.border};
  border-left: 1px solid ${({ theme }) => theme.app.border};
`;

const SummaryGold = styled.div<{ $isDone: boolean }>`
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.app.text.dark2};
  white-space: nowrap;
  opacity: ${({ $isDone }) => ($isDone ? 0.5 : 1)};
`;

const Empty = styled.div`
  width: 100%;
  padding: 40px 20px;
  border: 1px dashed ${({ theme }) => theme.app.border};
  border-radius: 8px;
  background: ${({ theme }) => theme.app.bg.white};
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;

  h3 {
    margin: 0;
    font-size: 16px;
    color: ${({ theme }) => theme.app.text.dark2};
  }

  p {
    margin: 0;
    font-size: 13px;
    color: ${({ theme }) => theme.app.text.light2};
  }
`;
